import React, { useState } from "react";
import {
  Card,
  DatePicker,
  Space,
  Button,
  Table,
  Typography,
  Input,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { useReportsServiceDownloadInventoryTransactionsReport, useReportsServiceGetInventoryTransactionsReport } from "api/queries";

const { Title } = Typography;

const PERIOD_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
  { label: "This Year", value: "This Year" },
];

const WarehouseTransferReport = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [periodFilter, setPeriodFilter] = useState("This Month");

  // Only send allowed filter fields to the API
  const offset = (page - 1) * limit;
  const { data, isLoading, isPending, isFetching } = useReportsServiceGetInventoryTransactionsReport({
    startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    offset,
    limit,
  });

  const { mutate: downloadReport, isPending: isDownloadPending } = useReportsServiceDownloadInventoryTransactionsReport();

  const columns = [
    {
      title: "ID",
      dataIndex: "transactionId",
      key: "transactionId",
    },
    {
      title: "Type",
      dataIndex: "transactionType",
      key: "transactionType",
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
    },
    {
      title: "Brand",
      dataIndex: "brandName",
      key: "brandName",
    },
    {
      title: "Category",
      dataIndex: "productCategoryName",
      key: "productCategoryName",
    },
    {
      title: "Subcategory",
      dataIndex: "productSubcategoryName",
      key: "productSubcategoryName",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "From Location",
      dataIndex: "locationFrom",
      key: "locationFrom",
      render: (loc: string) => loc || "-",
    },
    {
      title: "To Location",
      dataIndex: "locationTo",
      key: "locationTo",
      render: (loc: string) => loc || "-",
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => dayjs(date).format("DD/MM/YYYY HH:mm"),
    },
  ];

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
    }
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
    setPeriodFilter("Custom");
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
    setPeriodFilter("Custom");
  };

  const handleTableChange = (pagination: any) => {
    if (pagination && pagination.current && pagination.pageSize) {
      setPage(pagination.current || 1);
      setLimit(pagination.pageSize || 10);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleDownload = () => {
    downloadReport(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            window.open(response.downloadUrl, '_blank');
          }
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <div style={{ padding: "24px" }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Title level={2}>Inventory Transactions</Title>
              <Space>
                <Input.Search
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search"
                  style={{ width: 200 }}
                />
                <Select
                  value={periodFilter}
                  onChange={handlePeriodChange}
                  style={{ width: 120 }}
                >
                  {PERIOD_OPTIONS.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
                <DatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  style={{ width: 120 }}
                  placeholder="From"
                />
                <DatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  style={{ width: 120 }}
                  placeholder="To"
                />
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  loading={isDownloadPending}
                  onClick={handleDownload}
                >
                  Download Report
                </Button>
              </Space>
            </Space>
            <Table
              columns={columns}
              dataSource={data?.items}
              loading={isLoading || isPending || isFetching}
              rowKey={(item) => item.transactionId}
              scroll={{ x: "max-content" }}
              pagination={{
                current: data?.pagination?.offset !== undefined ? Math.floor(data.pagination.offset / data.pagination.limit) + 1 : page,
                pageSize: data?.pagination?.limit || limit,
                total: data?.pagination?.total || 0,
                onChange: (page, pageSize) => {
                  setPage(page);
                  setLimit(pageSize);
                },
                pageSizeOptions: [10, 20, 50, 100],
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              onChange={handleTableChange}
            />
          </Space>
        </Card>
      </div>
    </motion.div>
  );
};

export default WarehouseTransferReport;
